import { type MouseEvent, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { isModifiedClick } from './isModifiedClick';

function getExternalLinkHost(href: string): string {
    try {
        return new URL(href).hostname;
    } catch {
        return href;
    }
}

export function useExternalLinkConfirmation(): (event: MouseEvent<HTMLAnchorElement>, href: string) => void {
    const { openModal, closeModal } = useModal();

    return useCallback(
        (event: MouseEvent<HTMLAnchorElement>, href: string) => {
            if (isModifiedClick(event)) {
                return;
            }

            event.preventDefault();

            const host = getExternalLinkHost(href);

            openModal(
                <ConfirmationModal
                    titleId="ai-chat.external-link.title"
                    confirmId="ai-chat.external-link.confirm"
                    cancelId="ai-chat.external-link.cancel"
                    onCancel={closeModal}
                    onConfirm={() => {
                        window.open(href, '_blank', 'noopener,noreferrer');
                        closeModal();
                    }}
                    content={
                        <p className="text-sm leading-relaxed">
                            <FormattedMessage
                                id="ai-chat.external-link.body"
                                values={{
                                    host: (
                                        <code
                                            key="host"
                                            className="border-border rounded border px-1.5 py-0.5 font-mono text-[0.85em]"
                                        >
                                            {host}
                                        </code>
                                    ),
                                }}
                            />
                        </p>
                    }
                />,
                undefined,
                { zIndex: 'var(--z-floating-ai-dialog)' }
            );
        },
        [openModal, closeModal]
    );
}
